import {collection, doc, getDocs, query, where, runTransaction, serverTimestamp} from 'firebase/firestore';
import {getBusinessDateKey} from '../utils/businessDate';
import {roundMoney, toAmount} from './billingService';

const equal = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Only fields edited in this form may change. An unrelated newer edit survives.
export function mergeDiaryEdit(current, previous, payload, hotelId) {
  if (!previous || current.hotelId !== hotelId || payload.hotelId !== hotelId) throw new Error('Booking/hotel change ho gaya hai. Fresh booking kholein.');
  const next = {...current};
  for (const [key, value] of Object.entries(payload)) {
    if (['updatedAt', 'balanceAmount'].includes(key) || equal(value, previous[key])) continue;
    if (!equal(current[key], previous[key]) && !equal(current[key], value)) throw new Error(`Booking ${key} doosre manager ne badla hai. Refresh karke edit karein.`);
    next[key] = value;
  }
  next.balanceAmount = roundMoney(Math.max(0, toAmount(next.totalAmount) - toAmount(next.advanceAmount)));
  return next;
}

// Booking and its one receipt commit together, including advance corrections.
export async function commitBookingDiary({database, reference, hotelId, payload, previous, create = false}) {
  const eventAt = new Date().toISOString();
  const existingReceipts = create ? [] : (await getDocs(query(collection(database, 'dayBookEntries'), where('bookingDiaryId', '==', reference.id)))).docs;
  return runTransaction(database, async transaction => {
    const snapshot = await transaction.get(reference);
    if (create && snapshot.exists()) throw new Error('Booking already saved hai. Fresh diary kholein.');
    if (!create && !snapshot.exists()) throw new Error('Booking delete/change ho gayi hai.');
    const current = snapshot.exists() ? snapshot.data() : {};
    const next = create ? {...payload} : mergeDiaryEdit(current, previous, payload, hotelId);
    if (next.hotelId !== hotelId) throw new Error('Booking hotel mismatch.');
    const amount = roundMoney(toAmount(next.advanceAmount));
    if (amount < 0) throw new Error('Advance negative nahi ho sakta.');
    if (!current.dayBookAdvanceEntryId && existingReceipts.length > 1) throw new Error('Booking ki multiple legacy advance receipts hain. Pehle accounting verify karein.');
    const receiptId = current.dayBookAdvanceEntryId || existingReceipts[0]?.id || `BOOKING_ADVANCE_${reference.id}`;
    const receiptRef = doc(database, 'dayBookEntries', receiptId);
    const receipt = await transaction.get(receiptRef);
    const oldReceipt = receipt.exists() ? receipt.data() : null;
    if (oldReceipt && (oldReceipt.hotelId !== hotelId || oldReceipt.bookingDiaryId !== reference.id)) throw new Error('Booking receipt link mismatch.');
    const receivedAt = oldReceipt?.eventAt || current.advanceReceivedAt || eventAt;
    const room = String(next.roomPreference || '-').trim();
    const guestName = String(next.guestName || '').trim();
    if (amount > 0) transaction.set(receiptRef, {
      ...oldReceipt, hotelId, hotelName: next.hotelName || '', bookingDiaryId: reference.id,
      entryType: 'payment', category: 'booking_diary_advance', source: 'booking_diary',
      title: 'Booking Diary Advance Received', amount, paymentMode: next.paymentMode || 'Cash',
      transactionReference: next.transactionReference || '', room, guestName,
      eventAt: receivedAt, businessDate: oldReceipt?.businessDate || getBusinessDateKey(receivedAt),
      note: `Booking Diary advance • ${guestName || 'Guest'} • Check-In ${next.checkInDate || ''}`,
      ledgerRoom: room, ledgerGuestName: guestName,
      ledgerNote: `Booking Diary Advance • Check-In ${next.checkInDate || ''}`,
      ledgerTransactionReference: next.transactionReference || '', hideFromRegister: true,
      createdAt: oldReceipt?.createdAt || serverTimestamp(), updatedAt: serverTimestamp(),
    });
    else if (receipt.exists()) transaction.delete(receiptRef);
    transaction.set(reference, {...next, dayBookAdvanceEntryId: amount > 0 ? receiptId : '',
      advanceReceivedAt: amount > 0 ? receivedAt : null,
      createdAt: current.createdAt || serverTimestamp(), updatedAt: serverTimestamp()});
    return reference.id;
  });
}
